import {LogFilterStatus, Plan, PlanIds} from "./types";

export const HOOK_EVENT_EXTERNAL = 'EXTERNAL'
export const HOOK_EVENT_ORDER_CREATED = 'ORDER_CREATED'

export const HOOK_FIELDS_ON_CLIENT = [
    '_id', 'name', 'source', 'event', 'isEnabled', 'code', 'shopUrl',
    'webhookId', 'webhookSlug', 'polling', 'authentication',
    'preferences', 'notifications', 'environmentVariables'
]

// Client preferences keys
export const PREF_KEY_DISPLAY_WELCOME = 'displayWelcome'
export const PREF_KEY_PRESERVE_DATA_ON_UNINSTALL = 'preserveDataOnUninstall'
export const PREF_KEY_NOTIFICATION_EMAIL = 'notificationEmail'
export const PREF_KEY_RECEIVE_PRODUCT_UPDATES = 'receiveProductUpdates'
export const PREF_KEY_LOG_REDACTION_KEYS = 'logRedactionKeys'

export const LOG_REDACTION_KEY_MAX_LENGTH = 64;
export const LOG_REDACTION_KEYS_MAX_ITEMS = 50;

export const DECIMAL_POINT = '.';
export const THOUSAND_SEPARATOR = ',';

export const TRIAL_PERIOD_DAYS = 7;
export const TRIAL_END_APPROACHING_DAYS = 3;

export const PRO_LOG_RETENTION_DAYS = 14;
export const FREE_LOG_RETENTION_DAYS = 3;
export const TRIAL_LOG_RETENTION_DAYS = PRO_LOG_RETENTION_DAYS;

// Bulk replay (CLOUDHOOKS-458). Shared with the client: the confirm modal states the batch cap and the
// Logs tab renders a capped filtered count as "10,000+".
export const REPLAY_BATCH_MAX_RUNS = 5000; // most runs one bulk replay batch may hold (R10)
export const LOG_LIST_COUNT_CAP = 10000;   // the filtered log count stops counting past this (KTD-11)
// How long an ended batch's summary stays up when nobody dismisses it (the banner hides it past this), and after
// which the status route stops recomputing an unsettled batch's counts (KTD-16).
export const REPLAY_BATCH_SUMMARY_VISIBLE_MS = 7 * 24 * 60 * 60 * 1000;

// The Logs-tab status filters whose scope can contain finished (replayable) runs (CLOUDHOOKS-458, KTD-13). The
// one place a filter status is classified for bulk replay: the client picks the modal's explanation state from it
// (R16), the preview route short-circuits to zero replayable and the create route rejects outside it. Scheduled
// is absent: its scope is only unfinished retries. A status added later (e.g. 432's at merge, M1) is classified here.
export const REPLAYABLE_LOG_FILTER_STATUSES: ReadonlySet<LogFilterStatus> = new Set([
    LogFilterStatus.STATUS_FAILED,
    LogFilterStatus.STATUS_SUCCEEDED,
    LogFilterStatus.STATUS_UNPROCESSED,
    LogFilterStatus.STATUS_ALL,
]);

export const TRIAL_PLAN_ACTION_LIMIT = 1200
export const FREE_PLAN_ACTION_LIMIT = 100
export const FREE_PLAN_ACTION_THRESHOLD = 80

export const PLAN_TRIAL: Plan = {
    id: PlanIds.Trial,
    name: "Trial",
    price: [
        {threshold: Infinity, rate: 0},
    ],
    features: {
        logRetentionDays: TRIAL_LOG_RETENTION_DAYS,
        externalTriggers: true,
        environmentVariables: true,
        failedHookNotifications: true,
        hookReplay: true,
        actionLimit: TRIAL_PLAN_ACTION_LIMIT,
        rateLimitHandling: true // Trial exists to evaluate paid features (CLOUDHOOKS-435, F97)
    }
}

export const PLAN_FREE: Plan = {
    id: PlanIds.Free,
    name: "Starter",
    description: 'Free',
    price: [
        {threshold: FREE_PLAN_ACTION_LIMIT, rate: 0},
        {threshold: Infinity, rate: 0},
    ],
    features: {
        logRetentionDays: FREE_LOG_RETENTION_DAYS,
        externalTriggers: false,
        environmentVariables: false,
        failedHookNotifications: false,
        hookReplay: false,
        actionLimit: FREE_PLAN_ACTION_LIMIT,
        rateLimitHandling: false // paid feature — free plan keeps today's behavior (CLOUDHOOKS-435, R9/F42)
    }
}

export const PLAN_PRO: Plan = {
    id: PlanIds.Pro,
    name: "Pro",
    description: 'Pay as you go',
    price: [
        {threshold: 100, rate: 0},
        {threshold: 10000, rate: 1},
        {threshold: Infinity, rate: 0.5}
    ],
    features: {
        logRetentionDays: PRO_LOG_RETENTION_DAYS,
        externalTriggers: true,
        environmentVariables: true,
        failedHookNotifications: true,
        hookReplay: true,
        actionLimit: Infinity,
        rateLimitHandling: true // covers Pro AND Custom (getDefaultFeaturesForPlanId maps Custom -> PLAN_PRO)
    }
}

export const HTTP_PARAM_NAME_HOOK_ID = 'hookId'
export const HTTP_PARAM_NAME_LOG_ID = 'logId'